import { Controller } from "@hotwired/stimulus"
import { positionFixedDropdown, resetScrollHint } from "../utils/dropdown_utils"

// Replaces a native <select> with a styled dropdown that has a custom scrollbar.
// The native select is hidden but stays in the DOM for form submission.
// Dropdown uses position:fixed so overflow-hidden ancestors (e.g. table cards) do not clip it.
export default class extends Controller {
  static targets = ["nativeSelect", "trigger", "label", "dropdown", "clearBtn", "search", "noResults"]

  connect() {
    this.buildOptions()
    this.updateLabel()
    this.repositionHandler = this.repositionIfOpen.bind(this)
  }

  disconnect() {
    this.teardownFixedListeners()
  }

  toggle(event) {
    event.stopPropagation()
    const isOpen = !this.dropdownTarget.classList.contains("hidden")

    if (isOpen) {
      this.closeDropdown()
    } else {
      document.dispatchEvent(new CustomEvent("dropdown:close-all", { detail: { except: this.element } }))

      // Show before measuring so offsetHeight reflects the real rendered height.
      this.dropdownTarget.classList.remove("hidden")
      this.triggerTarget.setAttribute("aria-expanded", "true")
      positionFixedDropdown(this.dropdownTarget, this.triggerTarget)
      this.setupFixedListeners()
      resetScrollHint(this.dropdownTarget)

      if (this.hasSearchTarget) {
        this.searchTarget.value = ""
        this.filter()
        setTimeout(() => this.searchTarget.focus(), 10)
      }

      const selected = this.dropdownTarget.querySelector("[data-selected='true']")
      if (selected) {
        this.scrollSelectedOptionIntoView(selected)
      }
    }
  }

  closeDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.teardownFixedListeners()
  }

  setupFixedListeners() {
    window.addEventListener("resize", this.repositionHandler)
    window.addEventListener("scroll", this.repositionHandler, true)
  }

  teardownFixedListeners() {
    if (!this.repositionHandler) return
    window.removeEventListener("resize", this.repositionHandler)
    window.removeEventListener("scroll", this.repositionHandler, true)
  }

  repositionIfOpen() {
    if (this.dropdownTarget.classList.contains("hidden")) return
    positionFixedDropdown(this.dropdownTarget, this.triggerTarget)
  }

  handleKeydown(event) {
    if (event.key === "Enter") event.preventDefault()
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeDropdown()
      this.triggerTarget.focus()
    }
  }

  filter() {
    if (!this.hasSearchTarget) return

    const query = this.searchTarget.value.toLowerCase().trim()
    const buttons = this.dropdownTarget.querySelectorAll("[data-role='options'] button")
    let visibleCount = 0

    buttons.forEach(btn => {
      const text = btn.textContent.toLowerCase()
      if (query === "" || text.includes(query)) {
        btn.classList.remove("hidden")
        visibleCount++
      } else {
        btn.classList.add("hidden")
      }
    })

    if (this.hasNoResultsTarget) {
      if (visibleCount === 0) {
        this.noResultsTarget.classList.remove("hidden")
      } else {
        this.noResultsTarget.classList.add("hidden")
      }
    }
  }

  select(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const value = btn.dataset.value

    this.nativeSelectTarget.value = value
    this.nativeSelectTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.updateLabel()
    this.highlightSelected(value)
    this.closeDropdown()
    if (this.hasClearBtnTarget) this.clearBtnTarget.classList.remove("hidden")
  }

  buildOptions() {
    const container = this.dropdownTarget.querySelector("[data-role='options']")
    container.replaceChildren()

    Array.from(this.nativeSelectTarget.options).forEach(opt => {
      if (opt.value === "") return

      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.value = opt.value
      btn.dataset.action = "click->custom-select#select"
      btn.textContent = opt.textContent

      const isSelected = opt.selected && opt.value !== ""
      btn.dataset.selected = isSelected ? "true" : "false"

      if (isSelected) {
        btn.className = "w-full text-left px-3 py-1.5 text-sm bg-blue-100 text-blue-800"
      } else {
        btn.className = "w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 text-gray-700"
      }

      container.appendChild(btn)
    })
  }

  clear() {
    this.nativeSelectTarget.value = ""
    this.updateLabel()
    this.highlightSelected("")
    if (this.hasClearBtnTarget) this.clearBtnTarget.classList.add("hidden")
  }

  clearValue(event) {
    event.stopPropagation()
    this.clear()
    this.nativeSelectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateLabel() {
    const selected = this.nativeSelectTarget.selectedOptions[0]
    if (selected && selected.value !== "") {
      this.labelTarget.textContent = selected.textContent
      this.labelTarget.classList.remove("text-gray-400")
      this.labelTarget.classList.add("text-gray-900")
    } else {
      this.labelTarget.textContent = this.element.dataset.placeholder || "Select"
      this.labelTarget.classList.remove("text-gray-900")
      this.labelTarget.classList.add("text-gray-400")
    }
  }

  highlightSelected(value) {
    this.dropdownTarget.querySelectorAll("[data-role='options'] button").forEach(btn => {
      if (btn.dataset.value === value) {
        btn.className = "w-full text-left px-3 py-1.5 text-sm bg-blue-100 text-blue-800"
        btn.dataset.selected = "true"
      } else {
        btn.className = "w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 text-gray-700"
        btn.dataset.selected = "false"
      }
    })
  }

  scrollSelectedOptionIntoView(selected) {
    const optionsContainer = selected.closest(".max-h-52")
    if (!optionsContainer) return

    const selectedTop = selected.offsetTop
    const selectedBottom = selectedTop + selected.offsetHeight
    const viewTop = optionsContainer.scrollTop
    const viewBottom = viewTop + optionsContainer.clientHeight

    if (selectedTop < viewTop) {
      optionsContainer.scrollTop = selectedTop
      return
    }

    if (selectedBottom > viewBottom) {
      optionsContainer.scrollTop = selectedBottom - optionsContainer.clientHeight
    }
  }

  closeAll(event) {
    if (event.detail?.except !== this.element) {
      this.closeDropdown()
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

}
